import React, {useEffect, useRef, useState} from 'react';
import {Trans} from 'react-i18next';
import Toggle from 'react-toggle';
import Select from 'react-select';
import _isNull from 'lodash/isNull';
import classnames from 'classnames';
import Iframe from './Iframe/Iframe';
import templateUtils from '../../uiconfiguration/config/utils/templateUtils';
import _isEmpty from 'lodash/isEmpty';
import {getHtmlLanguages} from '../../../../utils/skincareUtils';
import cookieUtils from '../../../../utils/cookieUtils';
import InfoDialog from '../../../../components/dialogs/InfoDialog';
import langUtils from '../../../../utils/langUtils';
import {deviceOptions} from '../../uiconfiguration/config/utils/optionsUtils';
import './index.css';
import storeUtils from '../../../../utils/storeUtils';
import {useSelector} from 'react-redux';
import smbUtils from '../../../../utils/smbUtils';
import useWindowWidth from '../../../../components/hook/useWindowWidth';
import _get from 'lodash/get';
import Modal from 'react-bootstrap/Modal';

const iframeStyle = {
  border: 'none',
  backgroundColor: 'white',
  margin: 'auto',
  boxShadow: '0 0 5px #aaa',
  position: 'absolute',
  left: '50%',
  marginTop: '20px',
  transformOrigin: '0 0',
};

export default function PreviewModule({
  values,
  template,
  previewUrl,
  type,
  lang = {label: 'ENU', value: 'enu'},
  onChangeLanguage,
  onChangeDevice,
  isV2Above = true,
  deviceList = deviceOptions,
  langList = langUtils.getAppLangList(),
  isCustomized = false,
}) {
  const accountSummary = useSelector(
    state => state.components.auth.accountSummary
  );
  const {customerId} = smbUtils.customerInfo(accountSummary);
  const [show, setShow] = useState(true);
  const [config, setConfig] = useState(null);
  const containerRef = useRef(null);
  const [targetDomain, setTargetDomain] = useState('');
  const [enablePostMessage, setEnablePostMessage] = useState(true);
  const [tempConfig, setTempConfig] = useState();
  const [language, setLanguage] = useState(
    langList.find(l => l.value === cookieUtils.getCookie('appLang')) || lang
  );
  const [device, setDevice] = useState(
    deviceList.find(
      option => option.value === storeUtils.getUiConfigSettingDevice(customerId)
    ) || deviceList[0]
  );
  const [fullScreen, setFullScreen] = useState(false);
  const windowWidth = useWindowWidth();
  const isIpadDevice = windowWidth <= 1366;
  const isDesktopDevice = windowWidth > 1440;
  const isLandscape =
    _get(device, 'value') === deviceList.find(item => item.value === 'pad');
  const isPhone =
    _get(device, 'value') === deviceList.find(item => item.value === 'mobile');

  const getFrameScale = (width, height) => {
    if (_isNull(containerRef) || !containerRef.current) {
      return 1;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const baseWidth = rect.width - 40;
    const baseHeight = rect.height > 600 ? rect.height - 100 : rect.height - 50;
    if (height > baseHeight) {
      return [baseWidth / width, baseHeight / height].sort((a, b) => a - b)[0];
    }
    return width > baseWidth ? baseWidth / width : width / baseWidth;
  };
  const topPosition = () => {
    if (isIpadDevice) {
      if (isLandscape) {
        return '20%';
      }
      if (isPhone) {
        return '25%';
      }
      return '12%';
    }
    if (isDesktopDevice) {
      if (isLandscape) {
        return '5%';
      }
      if (isPhone) {
        return '10%';
      }
      return '-10%';
    }
    if (isLandscape) {
      return '-5%';
    }
    if (isPhone) {
      return '0%';
    }
    return '-28%';
  };

  const leftPosition = () => {
    if (isLandscape) {
      return '-120%';
    }
    if (isPhone) {
      return '-10%';
    }
    return '-75%';
  };

  const scaleMaxSize = () => {
    if (isIpadDevice) {
      if (isLandscape) {
        return 0.95;
      }
      if (isPhone) {
        return 1.7;
      }
      return 1.1;
    }
    if (isDesktopDevice) {
      if (isLandscape) {
        return 1.05;
      }
      if (isPhone) {
        return 1.2;
      }
      return 0.8;
    }
    if (isPhone) {
      return 0.9;
    }
    if (isLandscape) {
      return 0.85;
    }
    return 0.6;
  };

  const iframeZoomedStyle = {
    ...iframeStyle,
    left: leftPosition(),
    top: topPosition(),
    marginTop: '0px',
    zIndex: 1000,
  };

  useEffect(() => {
    if (previewUrl) {
      setTargetDomain('https://' + new URL(previewUrl).hostname);
    }
  }, [previewUrl]);

  useEffect(() => {
    if (tempConfig && JSON.stringify(tempConfig) === JSON.stringify(template))
      return;
    updateConfig();
  }, [values, template, device]);

  useEffect(() => {
    updateConfig();
  }, [language]);

  const updateConfig = () => {
    setTempConfig(template);
    if (!_isEmpty(template)) {
      if (type === 'LandingPage') {
        setConfig(templateUtils.toLandingPreview(template));
      } else if (type === 'ResultPage' || type === 'GetResult') {
        setConfig(templateUtils.toResultPreview(template));
      } else if (type === 'SimulationPage') {
        setConfig(templateUtils.toSimulationPreview(template));
      } else {
        setConfig(template);
      }
    }
  };

  return (
    <>
      <div className="m-portlet" style={{height: '100%'}}>
        <div className="m-portlet__head">
          <div className="m-portlet__head-tools">
            <div>
              <div className="kt-margin-r-30 kt-margin-l-30 d-flex align-items-center">
                <strong className="flex-grow-1">
                  {_get(device, 'label')}
                  {isLandscape && (
                    <InfoDialog
                      trigger={'click'}
                      placement="right"
                      description={
                        <Trans i18nKey="p.s.uiconfiguration.preview.landscape.desc">
                          Landscape Mode is supported only on iPads with the
                          front camera positioned on the long side. Please
                          verify your device's compatibility before applying the
                          Landscape Mode UI settings.
                        </Trans>
                      }
                    >
                      <label className="cursor-help">
                        &nbsp;
                        <i className="fa fa-question-circle" />
                      </label>
                    </InfoDialog>
                  )}
                </strong>
                <InfoDialog
                  trigger={'click'}
                  placement="auto"
                  popoverMaxWidth={'400px'}
                  title={
                    <>
                      <Trans i18nKey="p.s.u.c.preview.setting">
                        Preview Setting
                      </Trans>
                      <div className="float-right">
                        <i
                          className="fa fa-times cursor-pointer"
                          style={{fontSize: '20px'}}
                          onClick={() => document.body.click()}
                        />
                      </div>
                    </>
                  }
                  description={
                    <div className="d-flex flex-column kt-margin-t-10 kt-margin-b-10">
                      <div>
                        <div className="float-left kt-margin-r-20">
                          <Trans i18nKey="tutorials.steps.addskulooktour.preview.screen">
                            Preview Screen
                          </Trans>
                        </div>
                        <div className="float-right">
                          <Toggle
                            checked={show}
                            onChange={e => setShow(e.target.checked)}
                            icons={{unchecked: false}}
                          />
                        </div>
                      </div>
                      <div className="kt-margin-t-10">
                        <div className="float-left kt-margin-r-20 kt-margin-t-10">
                          <Trans i18nKey="c.l.sidebar.9160.title">Device</Trans>
                        </div>
                        <div className="float-right">
                          <div
                            style={{
                              width: '180px',
                            }}
                          >
                            <Select
                              defaultValue={device}
                              options={
                                isV2Above
                                  ? deviceList
                                  : deviceList.filter(
                                      o => o.value !== 'padLandscape'
                                    )
                              }
                              value={device}
                              onChange={o => {
                                setDevice(o);
                                onChangeDevice &&
                                  onChangeDevice(
                                    o.value === 'phone' ? 'mobile' : o.value
                                  );
                              }}
                              placeholder={
                                <Trans i18nKey="c.c.select.placeholder">
                                  Select...
                                </Trans>
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="kt-margin-t-10">
                        <div className="float-left kt-margin-r-20 kt-margin-t-10">
                          <Trans i18nKey="p.c.listrequests.language">
                            Language
                          </Trans>
                        </div>
                        <div className="float-right">
                          <div
                            style={{
                              width: '180px',
                            }}
                          >
                            <Select
                              options={langUtils.getAppLangList()}
                              onChange={o => {
                                setLanguage(o);
                                onChangeLanguage && onChangeLanguage(o);
                              }}
                              value={language}
                              isSearchable={false}
                              isClearable={false}
                              placeholder={
                                <Trans i18nKey="c.c.select.placeholder">
                                  Select...
                                </Trans>
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                >
                  <i
                    className="fa fa-cog cursor-pointer"
                    style={{fontSize: '26px'}}
                  />
                </InfoDialog>
                {isV2Above && windowWidth >= 1024 && (
                  <div>
                    <img
                      src={require('../../../../assets/icons/up-right-and-down-left-from-center-solid.svg')}
                      className="cursor-pointer kt-margin-l-10"
                      style={{width: '24px'}}
                      alt=""
                      onClick={() => setFullScreen(true)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className="m-portlet__body"
          style={{height: '80%'}}
          ref={containerRef}
        >
          {template && !_isNull(template) && previewUrl && (
            <div
              className={classnames({
                'iframe-container': true,
                'no-show': !show,
              })}
            >
              <div className="iframe-preview-container">
                <Iframe
                  language={language}
                  key={`${previewUrl}.'enu.skincare.${device}`}
                  src={`${previewUrl}?locale=${
                    getHtmlLanguages[language.value]
                  }&page_mode=preview&isPreview=true&isCustomizedPage=${isCustomized}`}
                  renderFn={'setPageConfigAndRerender'}
                  config={config}
                  style={{
                    ...iframeStyle,
                    width: `${
                      templateUtils.previewDevices[device.value].width
                    }px`,
                    height: `${
                      templateUtils.previewDevices[device.value].height
                    }px`,
                    transform: `scale(${getFrameScale(
                      templateUtils.previewDevices[device.value].width,
                      templateUtils.previewDevices[device.value].height
                    )}) translate(-50%)`,
                  }}
                  enablePostMessage={enablePostMessage}
                  targetDomain={targetDomain}
                  device={device.value}
                />
              </div>
            </div>
          )}
        </div>
        {template && !_isNull(template) && previewUrl && isCustomized && (
          <div className="mr-4 ml-4">
            <Trans i18nKey="p.s.uiconfiguration.customization.preview.desc">
              * This preview shows only the layout template. Your actual
              settings (such as skin concern orders) will be applied from saved
              data and visible in the app interface.
            </Trans>
          </div>
        )}
        <Modal
          size="sm"
          show={fullScreen}
          onHide={() => {
            setFullScreen(false);
          }}
          centered={true}
          dialogClassName="fullscreen-modal"
        >
          <Modal.Body className="fullscreen-modal-overlay">
            <div className="fullscreen-center-wrapper">
              <div className="iframe_preview-module-expand">
                <div className="iframe-preview-container">
                  <Iframe
                    language={language}
                    key={`${previewUrl}.'enu.skincare.${device}`}
                    src={`${previewUrl}?locale=${
                      getHtmlLanguages[language.value]
                    }&page_mode=preview&isPreview=true&isCustomizedPage=${isCustomized}`}
                    renderFn={'setPageConfigAndRerender'}
                    config={config}
                    style={{
                      ...iframeStyle,
                      ...iframeZoomedStyle,
                      width: `${
                        templateUtils.previewDevices[device.value].width
                      }px`,
                      height: `${
                        templateUtils.previewDevices[device.value].height
                      }px`,
                      transform: `scale(${Math.min(
                        getFrameScale(
                          templateUtils.previewDevices[device.value].width,
                          templateUtils.previewDevices[device.value].height
                        ) * templateUtils.previewDevices[device.value].zoom,
                        scaleMaxSize()
                      )})`,
                      transformOrigin: 'center center',
                    }}
                    enablePostMessage={enablePostMessage}
                    targetDomain={targetDomain}
                    device={device.value}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}
